/**
 * AdjustLotsPage — CRUD cho bảng adjust_lots (Sprint 5).
 * Type AdjustLot fields: adjust_lot_id (PK), futures_month, adjust_lot, note.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import { useForm } from 'react-hook-form'

import ConfirmDialog from '@/components/ConfirmDialog'
import DataTableMUI from '@/components/DataTableMUI'
import FormDialog from '@/components/FormDialog'
import PageDataState from '@/components/PageDataState'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useListAdjustLotsQuery,
  useCreateAdjustLotMutation,
  useUpdateAdjustLotMutation,
  useDeleteAdjustLotMutation,
} from '@/store/api/masterDataCrudApi'
import { useDebounce } from '@/hooks/useDebounce'
import { useSnackbar } from '@/hooks/useSnackbar'
import { getErrorMessage } from '@/utils/error'
import type { AdjustLot } from '@/types/api'

interface ALFormValues {
  futures_month: string
  adjust_lot: string
  note: string
}

const EMPTY: ALFormValues = { futures_month: '', adjust_lot: '', note: '' }

export function AdjustLotsPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 })
  const [editing, setEditing] = useState<AdjustLot | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<AdjustLot | null>(null)

  const { data, isLoading, error, isFetching } = useListAdjustLotsQuery({
    q: debouncedSearch || undefined,
    page: pagination.page + 1,
    page_size: pagination.pageSize,
  })

  const [createAdjustLot, { isLoading: creating }] = useCreateAdjustLotMutation()
  const [updateAdjustLot, { isLoading: updating }] = useUpdateAdjustLotMutation()
  const [deleteAdjustLot, { isLoading: deleting }] = useDeleteAdjustLotMutation()

  const dialogOpen = createOpen || !!editing
  const isEdit = !!editing
  const dialogLoading = creating || updating

  const { register, handleSubmit, reset } = useForm<ALFormValues>({ defaultValues: EMPTY })

  useEffect(() => {
    if (!dialogOpen) return
    if (isEdit && editing) {
      reset({
        futures_month: editing.futures_month ?? '',
        adjust_lot: editing.adjust_lot != null ? String(editing.adjust_lot) : '',
        note: editing.note ?? '',
      })
    } else {
      reset(EMPTY)
    }
  }, [dialogOpen, isEdit, editing, reset])

  const rows = useMemo(
    () => (data?.items ?? []).map((r) => ({ id: r.adjust_lot_id ?? r.futures_month, ...r })),
    [data],
  )

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: 'futures_month', headerName: 'Futures Month', width: 180 },
      { field: 'adjust_lot', headerName: 'Adjust Lot', width: 140, type: 'number' },
      { field: 'note', headerName: 'Note', flex: 1, minWidth: 200 },
      {
        field: '_actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit">
              <IconButton size="small" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setEditing(params.row as AdjustLot) }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setConfirmDelete(params.row as AdjustLot) }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  )

  const handleCloseDialog = () => { setCreateOpen(false); setEditing(null) }

  const onSubmit = handleSubmit(async (values) => {
    const payload: AdjustLot = {
      futures_month: values.futures_month.trim(),
      adjust_lot: Number(values.adjust_lot),
      note: values.note || null,
    }
    try {
      if (isEdit && editing) {
        const id = editing.adjust_lot_id ?? editing.futures_month
        await updateAdjustLot({ id, body: payload }).unwrap()
        snack.success(`Updated "${editing.futures_month}"`)
      } else {
        await createAdjustLot(payload).unwrap()
        snack.success(`Created "${payload.futures_month}"`)
      }
      handleCloseDialog()
    } catch (err) {
      snack.error(getErrorMessage(err, isEdit ? 'Update failed' : 'Create failed'))
    }
  })

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    const id = confirmDelete.adjust_lot_id ?? confirmDelete.futures_month
    try {
      await deleteAdjustLot(id).unwrap()
      snack.success(`Deleted "${confirmDelete.futures_month}"`)
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete failed'))
    }
  }

  const isEmpty = !isLoading && !error && (data?.items?.length ?? 0) === 0

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700}>Adjust Lots</Typography>
        <Button variant="contained" startIcon={<AddIcon />} disabled={!canEdit} onClick={() => setCreateOpen(true)}>
          New Adjust Lot
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <TextField
          label="Search"
          placeholder="Futures month..."
          size="small"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPagination((p) => ({ ...p, page: 0 })) }}
          sx={{ minWidth: 260 }}
        />
      </Paper>

      <PageDataState loading={isLoading} error={error} empty={isEmpty}>
        <Paper>
          <DataTableMUI
            rows={rows}
            columns={columns}
            loading={isFetching}
            paginationMode="server"
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            rowCount={data?.total ?? 0}
            onRowClick={(p) => setEditing(p.row as AdjustLot)}
          />
        </Paper>
      </PageDataState>

      <FormDialog
        open={dialogOpen}
        title={isEdit ? 'Edit Adjust Lot' : 'New Adjust Lot'}
        loading={dialogLoading}
        onClose={handleCloseDialog}
        onSubmit={() => void onSubmit()}
      >
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField label="Futures Month *" size="small" fullWidth {...register('futures_month')} disabled={isEdit} />
          <TextField label="Adjust Lot" size="small" fullWidth type="number" {...register('adjust_lot')} />
          <TextField label="Note" size="small" fullWidth multiline rows={2} {...register('note')} />
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Adjust Lot"
        message={confirmDelete ? `Are you sure you want to delete "${confirmDelete.futures_month}"?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  )
}

export default AdjustLotsPage
